import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createBlock as _createBlock, withKeys as _withKeys, createStaticVNode as _createStaticVNode } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "mt-4 centered"
};
const _hoisted_2 = {
    key: 1,
    class: "tagViews"
};
const _hoisted_3 = {
    key: 0,
    class: "tagsList"
};
const _hoisted_4 = ["onClick"];
const _hoisted_5 = { class: "raceTop" };
const _hoisted_6 = { class: "raceTag" };
const _hoisted_7 = {
    key: 0,
    class: "raceAmountSkeleton"
};
const _hoisted_8 = {
    key: 1,
    class: "raceAmount"
};
const _hoisted_9 = { class: "raceTrack" };
const _hoisted_10 = {
    key: 0,
    class: "raceBarSkeleton"
};
const _hoisted_11 = {
    key: 0,
    class: "spotlight"
};
const _hoisted_12 = { class: "spotlightTotal" };
const _hoisted_13 = {
    key: 0,
    class: "spotlightSection"
};
const _hoisted_14 = { class: "merchantName" };
const _hoisted_15 = { class: "merchantAmount" };
const _hoisted_16 = { class: "spotlightSection" };
const _hoisted_17 = {
    key: 0,
    class: "dupeError"
};
const _hoisted_18 = { class: "drawer-tx-wrap" };
const _hoisted_19 = { class: "info-drawer-content" };
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { GraphChart } from 'echarts/charts';
import VChart from 'vue-echarts';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useTransactions } from '@/composables/useTransactions';
import { useBanking, pendingBankingRevalidate } from '@/composables/useBanking';
import { useEmployer } from '@/composables/useEmployer';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import TransactionsList from './elements/TransactionsList.vue';
import SwipeActionsContainer from './elements/SwipeActionsContainer.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
import MGTextField from './elements/MGTextField.vue';
import MGButton from './elements/MGButton.vue';
import MGDialog from './elements/MGDialog.vue';
const showCreateTagCta = true;
const showEditTagCta = true;
export default /*@__PURE__*/ _defineComponent({
    __name: 'SpendByCustomerTagPage',
    setup(__props) {
        use([CanvasRenderer, GraphChart]);
        const state = useAppState();
        const router = useRouter();
        useAuth();
        const { getCustomer, getCustomerTags, getTransactionsForCustomerTag, deleteCustomerTag, clearCustomerTagsCache, getEmployer } = useCustomer();
        const { isMoneyGuided } = useEmployer();
        const transactionsApi = useTransactions();
        const { getBankingInfo } = useBanking();
        // CTA toggles — flip to false to exclude without removing the markup.
        const customerTags = ref(null);
        const swipeContainer = ref(null);
        // Lower-cased tag names currently being reconciled by a post-write poll — their
        // rows show a skeleton loader until the fresh (hash-changed) payload lands.
        const reconcilingTags = ref(new Set());
        const spotlightOpen = ref(false);
        const infoOpen = ref(false);
        const selectedTag = ref(null);
        const barsAnimated = ref(false);
        const nameDialogOpen = ref(false);
        const pickerOpen = ref(false);
        const newTagName = ref('');
        const pickerTransactions = ref([]);
        // When editing an existing tag, the tag's own /Transactions payload (loaded for
        // the spotlight) — authoritative for which transactions carry the tag, unlike
        // the cached banking recentTransactions which can be stale after a tag write.
        // Null when creating a brand-new tag.
        const editTagTransactions = ref(null);
        const newTagSelectedIds = ref(new Set());
        const pickerInitialSelectedIds = ref(new Set());
        const newTagSaving = ref(false);
        const viewMode = ref(
        // (localStorage.getItem('tagViewMode') as 'bars' | 'cloud') || 'bars'
        'bars');
        watch(viewMode, (v) => localStorage.setItem('tagViewMode', v));
        // The cloud packs to the chart container's real size — measured once the
        // cloud view is in the DOM, instead of re-deriving page chrome from 100vh.
        const cloudWrapRef = ref(null);
        const cloudSize = ref(null);
        watch([viewMode, () => customerTags.value?.length], () => {
            if (viewMode.value !== 'cloud')
                return;
            nextTick(() => {
                const el = cloudWrapRef.value;
                if (el)
                    cloudSize.value = { w: el.clientWidth, h: el.clientHeight };
            });
        }, { immediate: true });
        // Sort by absolute total desc — spending is stored as negative amounts.
        const sortedTags = computed(() => {
            if (!customerTags.value)
                return null;
            return [...customerTags.value].sort((a, b) => Math.abs(b.total ?? 0) - Math.abs(a.total ?? 0));
        });
        // Reconciling tag names that have no row yet — i.e. a brand-new tag whose
        // create-poll hasn't landed. Rendered as skeleton rows so the user sees the
        // new tag appear immediately while the list poll catches up.
        const pendingNewTags = computed(() => {
            const existing = new Set((customerTags.value ?? []).map((t) => (t.tag ?? '').toLowerCase()));
            return [...reconcilingTags.value].filter(name => !existing.has(name));
        });
        const maxTotal = computed(() => {
            const tags = sortedTags.value ?? [];
            const max = tags.reduce((m, t) => Math.max(m, Math.abs(t.total ?? 0)), 0);
            return max || 1; // avoid div/0 before per-tag totals resolve
        });
        // Top 3 merchants for the selected tag, by absolute amount.
        const topMerchants = computed(() => {
            const groups = selectedTag.value?.groupedTransactions ?? [];
            const totals = new Map();
            for (const g of groups) {
                for (const tx of g) {
                    const name = tx.merchant?.name ?? tx.merchantName ?? '(unknown)';
                    totals.set(name, (totals.get(name) ?? 0) + Math.abs(tx.amount ?? 0));
                }
            }
            return Array.from(totals.entries())
                .map(([name, total]) => ({ name, total }))
                .sort((a, b) => b.total - a.total)
                .slice(0, 3);
        });
        // Bubble cloud: size by sqrt(total/max) so smaller tags still read; colour
        // is derived from the tag's biggest-spend merchant's category colour, tying
        // the cloud back to the rest of the app's category palette.
        const tagDominantColour = computed(() => {
            const map = new Map();
            for (const tag of customerTags.value ?? []) {
                const groups = tag.groupedTransactions ?? [];
                // Sum abs spend per merchant, remembering its category colour.
                const byMerchant = new Map();
                for (const g of groups) {
                    for (const tx of g) {
                        const name = tx.merchant?.name ?? tx.merchantName ?? '(unknown)';
                        const colour = tx.effectiveClassification?.color ?? null;
                        const e = byMerchant.get(name) ?? { total: 0, colour: null };
                        e.total += Math.abs(tx.amount ?? 0);
                        if (!e.colour && colour)
                            e.colour = colour;
                        byMerchant.set(name, e);
                    }
                }
                let best = null;
                for (const v of byMerchant.values()) {
                    if (!best || v.total > best.total)
                        best = v;
                }
                // Fallback: brand dark purple if we couldn't resolve a category colour.
                map.set(tag.id, best?.colour ?? '#332847');
            }
            return map;
        });
        // Mix a hex colour towards white — used for the tag lozenge so it reads as a
        // lighter shade of its bubble.
        function lightenHex(hex, amount = 0.8) {
            if (!hex.startsWith('#') || hex.length < 7)
                return '#ffffff';
            const mix = (c) => Math.round(c + (255 - c) * amount);
            const r = mix(parseInt(hex.substring(1, 3), 16));
            const g = mix(parseInt(hex.substring(3, 5), 16));
            const b = mix(parseInt(hex.substring(5, 7), 16));
            return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
        }
        // Pick a readable text colour given a background hex. Returns hex (not a CSS
        // var) because the cloud labels render on an ECharts canvas.
        function textColourFor(bg) {
            if (!bg.startsWith('#') || bg.length < 7)
                return '#ffffff';
            const r = parseInt(bg.substring(1, 3), 16);
            const g = parseInt(bg.substring(3, 5), 16);
            const b = parseInt(bg.substring(5, 7), 16);
            const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            return lum > 0.6 ? '#332847' : '#ffffff';
        }
        // Every bar fills the full track — it's a category breakdown, not a race. The
        // coloured segments inside split that full width proportionally per category.
        function raceBarStyle(_t) {
            return { width: barsAnimated.value ? '100%' : '0%' };
        }
        // Break a tag's spend into coloured segments — one per category colour, sized
        // as a share of the tag's own total, largest first — for a multi-colour bar.
        function tagSegments(t) {
            const groups = t.groupedTransactions ?? [];
            const byColour = new Map();
            for (const g of groups) {
                for (const tx of g) {
                    const colour = tx.effectiveClassification?.color ?? '#332847';
                    byColour.set(colour, (byColour.get(colour) ?? 0) + Math.abs(tx.amount ?? 0));
                }
            }
            if (byColour.size === 0)
                return [{ color: '#332847', pct: 100 }];
            const total = [...byColour.values()].reduce((a, b) => a + b, 0) || 1;
            const segs = [...byColour.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([color, amt]) => ({ color, pct: (amt / total) * 100 }));
            // Give every segment a minimum visible width so tiny categories don't vanish,
            // then claw the extra width back from the larger segments (proportional to
            // their size) so the bar still sums to 100%.
            const MIN_PCT = 5;
            const below = segs.filter(s => s.pct < MIN_PCT);
            if (below.length) {
                const deficit = below.reduce((sum, s) => sum + (MIN_PCT - s.pct), 0);
                const above = segs.filter(s => s.pct >= MIN_PCT);
                const aboveTotal = above.reduce((sum, s) => sum + s.pct, 0);
                below.forEach(s => { s.pct = MIN_PCT; });
                if (aboveTotal > 0)
                    above.forEach(s => { s.pct -= deficit * (s.pct / aboveTotal); });
            }
            return segs;
        }
        // Floating bubble cloud — ECharts graph with no links and fixed circle-packed
        // positions (force layout has no collision detection, so bubbles either
        // overlapped or flew apart). Largest bubble sits in the centre; each next one
        // walks a spiral outward until it fits without touching anything placed.
        const cloudOption = computed(() => {
            // Wait for the container measurement — renders empty for one tick.
            if (!cloudSize.value)
                return {};
            const tags = sortedTags.value ?? [];
            const minSize = 64;
            const maxSize = 140;
            const PAD = 6; // minimum gap between bubble edges
            const placed = [];
            function place(r) {
                if (placed.length === 0) {
                    placed.push({ x: 0, y: 0, r });
                    return { x: 0, y: 0 };
                }
                for (let angle = 0; angle < 1000; angle += 0.2) {
                    const radius = angle * 2.5;
                    const x = radius * Math.cos(angle);
                    const y = radius * Math.sin(angle);
                    if (placed.every(p => Math.hypot(p.x - x, p.y - y) >= p.r + r + PAD)) {
                        placed.push({ x, y, r });
                        return { x, y };
                    }
                }
                // Unreachable in practice — the spiral above always finds a gap long
                // before 1000 radians for any plausible tag count.
                placed.push({ x: 0, y: 0, r });
                return { x: 0, y: 0 };
            }
            // sortedTags is already largest-first, which packing relies on.
            const packed = tags.map((t) => {
                const ratio = Math.abs(t.total ?? 0) / maxTotal.value;
                const size = Math.round(minSize + (maxSize - minSize) * Math.sqrt(ratio));
                return { t, size, pos: place(size / 2) };
            });
            // "Create new tag" bubble joins the packing like any other circle.
            const CREATE_SIZE = 96;
            const createPos = place(CREATE_SIZE / 2);
            // Scale everything down together if the packed cloud is bigger than the
            // container — distances and radii shrink in step, so nothing re-overlaps.
            const bound = placed.reduce((m, p) => Math.max(m, Math.abs(p.x) + p.r, Math.abs(p.y) + p.r), 1);
            const availW = (cloudSize.value.w - 8) / 2;
            const availH = (cloudSize.value.h - 8) / 2;
            const scale = Math.min(1, availW / bound, availH / bound);
            const nodes = packed.map(({ t, size, pos }) => {
                const colour = tagDominantColour.value.get(t.id) ?? '#332847';
                return {
                    id: t.id,
                    name: '#' + t.tag,
                    x: pos.x * scale,
                    y: pos.y * scale,
                    symbolSize: size * scale,
                    // Maps onto the force.repulsion range — bigger bubbles push harder.
                    value: size * scale,
                    itemStyle: { color: colour },
                    label: {
                        show: true,
                        position: 'inside',
                        align: 'center',
                        formatter: '{tag|#' + t.tag + '}\n{amt|' + state.formatter.format(Math.abs(t.total ?? 0)) + '}',
                        rich: {
                            // Lozenge in a lighter shade of the bubble's own colour, so the
                            // tag stays readable when it's wider than the bubble.
                            tag: {
                                fontSize: 12,
                                color: '#332847',
                                backgroundColor: lightenHex(colour),
                                borderRadius: 10,
                                padding: [3, 8],
                                align: 'center',
                            },
                            amt: { fontSize: 10, color: textColourFor(colour), opacity: 0.85, padding: [4, 0, 0, 0], align: 'center' },
                        },
                    },
                    _tagId: t.id,
                };
            });
            // Dashed "create" bubble, matching the old CSS cloud's CTA.
            nodes.push({
                id: '_create',
                name: 'Create new tag',
                x: createPos.x * scale,
                y: createPos.y * scale,
                symbolSize: CREATE_SIZE * scale,
                value: CREATE_SIZE * scale,
                itemStyle: {
                    color: 'transparent',
                    borderColor: '#332847',
                    borderWidth: 2,
                    borderType: 'dashed',
                },
                label: {
                    show: true,
                    position: 'inside',
                    align: 'center',
                    formatter: '{plus|+}\n{lbl|Create new tag}',
                    rich: {
                        plus: { fontSize: 26, color: '#332847', align: 'center' },
                        lbl: { fontSize: 10, color: '#332847', align: 'center', padding: [2, 0, 0, 0] },
                    },
                },
                _create: true,
            });
            return {
                series: [{
                        type: 'graph',
                        layout: 'force',
                        roam: false,
                        draggable: true,
                        nodes,
                        links: [],
                        // initLayout 'none' starts the simulation from our packed positions,
                        // so the force only nudges gently instead of resolving overlaps from
                        // random spots. Gravity keeps the cloud centred on screen.
                        force: {
                            initLayout: 'none',
                            repulsion: [200, 600],
                            gravity: 0.15,
                            friction: 0.2,
                            layoutAnimation: true,
                        },
                    }],
            };
        });
        function onCloudClick(params) {
            if (params.dataType !== 'node')
                return;
            // zrender handles the touch itself; without this the browser then fires a
            // synthetic click that lands on the freshly-opened drawer's overlay and
            // closes it instantly.
            params.event?.event?.preventDefault?.();
            if (params.data._create) {
                openCreateTag();
                return;
            }
            const tag = (customerTags.value ?? []).find((t) => t.id === params.data._tagId);
            if (tag)
                openSpotlight(tag);
        }
        function isReconciling(t) {
            return reconcilingTags.value.has((t.tag ?? '').toLowerCase());
        }
        function openSpotlight(t) {
            selectedTag.value = t;
            spotlightOpen.value = true;
        }
        function goToTransaction(t) {
            if (!t?.id)
                return;
            router.push('/TransactionDetail/' + t.id);
        }
        function handleAction({ item, action } = { item: null, action: null }) {
            if (!item || !action)
                return;
            if (action.type === 'delete') {
                // Keep the row in place showing a skeleton until the poll confirms the
                // deletion, rather than removing it optimistically. Reset the swipe so the
                // row sits closed behind the skeleton.
                reconcilingTags.value = new Set(reconcilingTags.value).add((item.tag ?? '').toLowerCase());
                swipeContainer.value?.resetSwipeState?.(item);
                // Fan delete across every backing id when the bubble represents merged
                // duplicates (same tag name, different server ids).
                const ids = (item.ids && item.ids.length ? item.ids : [item.id]);
                let remaining = ids.length;
                ids.forEach(id => {
                    deleteCustomerTag(id, () => {
                        remaining--;
                        // Don't clear the list cache — the poll needs it as its hash baseline.
                        // Reconcile by SWR+polling until the server payload drops the tag.
                        if (remaining === 0)
                            onLoad(true);
                    });
                });
            }
        }
        function openCreateTag() {
            newTagName.value = '';
            pickerTransactions.value = [];
            editTagTransactions.value = null;
            newTagSelectedIds.value = new Set();
            pickerInitialSelectedIds.value = new Set();
            nameDialogOpen.value = true;
        }
        // Build the picker's transaction list, deduped by id and regrouped by day.
        // When editing an existing tag, the cached banking recentTransactions can be
        // stale w.r.t. the latest tag write, so overlay the tag's authoritative per-tag
        // transactions: keep each banking row's full fields (e.g. bankAccountID) but take
        // its customerTags from the fresh per-tag payload, and append any tagged
        // transactions that fall outside the recent window. Then refresh pre-selection.
        function buildPickerTransactions() {
            const editGroups = editTagTransactions.value ?? [];
            const recentGroups = state.bankInfo?.recentTransactions ?? [];
            // Fresh customerTags keyed by transaction id, from the per-tag endpoint.
            const freshTags = new Map();
            for (const g of editGroups)
                for (const t of (g ?? [])) {
                    if (t?.id)
                        freshTags.set(t.id, t.customerTags ?? []);
                }
            const seen = new Set();
            const byDay = new Map();
            const push = (t) => {
                const key = t.timestampString ?? '';
                const arr = byDay.get(key) ?? [];
                arr.push(t);
                byDay.set(key, arr);
            };
            // Banking rows first (canonical objects), with fresh tags overlaid.
            for (const g of recentGroups)
                for (const t of (g ?? [])) {
                    if (!t?.id || seen.has(t.id))
                        continue;
                    seen.add(t.id);
                    push(freshTags.has(t.id) ? { ...t, customerTags: freshTags.get(t.id) } : t);
                }
            // Then tagged transactions that fall outside the recent window.
            for (const g of editGroups)
                for (const t of (g ?? [])) {
                    if (!t?.id || seen.has(t.id))
                        continue;
                    seen.add(t.id);
                    push(t);
                }
            pickerTransactions.value = Array.from(byDay.values())
                .sort((a, b) => new Date(b[0].timestamp).getTime() - new Date(a[0].timestamp).getTime());
            refreshPreselection();
        }
        // Walk the picker's transactions and pre-select any that already carry the
        // active tag name. Called whenever the picker's transaction list changes (open,
        // search-back, clear) so checkboxes always reflect server state.
        function refreshPreselection() {
            const name = trimmedNewTagName.value.toLowerCase();
            if (!name)
                return;
            const next = new Set(newTagSelectedIds.value);
            pickerTransactions.value.forEach((g) => g.forEach((t) => {
                const has = (t.customerTags ?? []).some((ct) => (ct.tag ?? '').toLowerCase() === name);
                if (has) {
                    next.add(t.id);
                    pickerInitialSelectedIds.value.add(t.id);
                }
            }));
            newTagSelectedIds.value = next;
        }
        // Picker display order: float the pre-selected (already-tagged) transactions to
        // the top, keeping each section in date-desc order. Uses pickerInitialSelectedIds
        // (not the live ticks) so rows don't jump around when the user un-ticks them.
        // pickerTransactions stays the source of truth for saving.
        const pickerDisplayTransactions = computed(() => {
            const pre = pickerInitialSelectedIds.value;
            if (pre.size === 0)
                return pickerTransactions.value;
            const taggedGroups = [];
            const otherGroups = [];
            pickerTransactions.value.forEach((g) => {
                const tagged = g.filter((t) => pre.has(t.id));
                const rest = g.filter((t) => !pre.has(t.id));
                if (tagged.length)
                    taggedGroups.push(tagged);
                if (rest.length)
                    otherGroups.push(rest);
            });
            return [...taggedGroups, ...otherGroups];
        });
        const trimmedNewTagName = computed(() => newTagName.value.trim().replace(/^#/, ''));
        // True when the name typed in the create dialog matches an existing tag
        // (case-insensitive). Only relevant for the create-new path — openAddToTag
        // skips the dialog entirely, so adding to an existing tag isn't blocked.
        const nameIsDuplicate = computed(() => {
            const name = trimmedNewTagName.value.toLowerCase();
            if (!name)
                return false;
            return (customerTags.value ?? []).some((t) => (t.tag ?? '').toLowerCase() === name);
        });
        function confirmTagName() {
            if (!trimmedNewTagName.value || nameIsDuplicate.value)
                return;
            nameDialogOpen.value = false;
            openPicker();
        }
        function openPicker() {
            pickerOpen.value = true;
            // Paint instantly from the cached banking transactions overlaid with the
            // edited tag's authoritative rows, then revalidate banking in the background
            // and rebuild (the banking cache can lag the user's latest tag write).
            buildPickerTransactions();
            getBankingInfo(() => buildPickerTransactions());
        }
        function openAddToTag(tag) {
            if (!tag?.tag)
                return;
            newTagName.value = tag.tag;
            // Seed the authoritative per-tag transactions (loaded for the spotlight) so
            // pre-selection reflects the latest tag state even if banking is stale.
            editTagTransactions.value = tag.groupedTransactions ?? null;
            newTagSelectedIds.value = new Set();
            pickerInitialSelectedIds.value = new Set();
            spotlightOpen.value = false;
            openPicker();
        }
        function onPickerSearchBack(query) {
            transactionsApi.searchTransactions(query, (result) => {
                // Merge results into the existing window, dedupe by id, regroup by day —
                // mirrors MoneyPage's searchBackTransactions so we don't lose locally
                // filtered txns that didn't match the merchant-name server search.
                const seen = new Set();
                const byDay = new Map();
                const ingest = (groups) => {
                    for (const g of groups ?? []) {
                        for (const t of g ?? []) {
                            if (!t?.id || seen.has(t.id))
                                continue;
                            seen.add(t.id);
                            const key = t.timestampString ?? '';
                            const arr = byDay.get(key) ?? [];
                            arr.push(t);
                            byDay.set(key, arr);
                        }
                    }
                };
                ingest(pickerTransactions.value);
                ingest(result);
                pickerTransactions.value = Array.from(byDay.values())
                    .sort((a, b) => new Date(b[0].timestamp).getTime() - new Date(a[0].timestamp).getTime());
                refreshPreselection();
            });
        }
        function toggleNewTagTransaction(t) {
            if (!t?.id)
                return;
            const next = new Set(newTagSelectedIds.value);
            if (next.has(t.id))
                next.delete(t.id);
            else
                next.add(t.id);
            newTagSelectedIds.value = next;
        }
        // Adds = currently selected but didn't originally have the tag.
        // Removes = originally pre-selected (had the tag) but now unticked.
        const pickerAddCount = computed(() => {
            let n = 0;
            newTagSelectedIds.value.forEach(id => { if (!pickerInitialSelectedIds.value.has(id))
                n++; });
            return n;
        });
        const pickerRemoveCount = computed(() => {
            let n = 0;
            pickerInitialSelectedIds.value.forEach(id => { if (!newTagSelectedIds.value.has(id))
                n++; });
            return n;
        });
        const canSaveNewTag = computed(() => trimmedNewTagName.value.length > 0
            && (pickerAddCount.value > 0 || pickerRemoveCount.value > 0)
            && !newTagSaving.value);
        const saveButtonText = computed(() => {
            if (newTagSaving.value)
                return 'Saving…';
            const adds = pickerAddCount.value;
            const removes = pickerRemoveCount.value;
            const parts = [];
            if (adds > 0)
                parts.push('Add to ' + adds);
            if (removes > 0)
                parts.push('Remove from ' + removes);
            const total = adds + removes;
            if (parts.length === 0)
                return 'Save';
            return parts.join(', ') + ' transaction' + (total === 1 ? '' : 's');
        });
        function saveNewTag() {
            if (!canSaveNewTag.value)
                return;
            const name = trimmedNewTagName.value;
            const nameLc = name.toLowerCase();
            const allTx = pickerTransactions.value.flat();
            // Collect every tx the user touched — selected ones that weren't pre-tagged
            // (adds) AND pre-tagged ones they unticked (removes). Anything unchanged
            // stays as-is.
            const targets = allTx.filter((t) => {
                const wasOn = pickerInitialSelectedIds.value.has(t.id);
                const isOn = newTagSelectedIds.value.has(t.id);
                return wasOn !== isOn;
            });
            if (targets.length === 0)
                return;
            newTagSaving.value = true;
            // Send updates sequentially — fire the next only after the previous responds.
            // Firing them in parallel races the backend: for a brand-new tag every request
            // carries the name with no id, so each concurrent write checks "does this tag
            // exist?", all see "no" before any has committed, and each creates a duplicate
            // record. Sequencing lets the first write commit the tag so the rest reuse it.
            const processNext = (i) => {
                if (i >= targets.length) {
                    const isExistingTag = (customerTags.value ?? []).some((t) => (t.tag ?? '').toLowerCase() === nameLc);
                    // Skeleton the edited tag's row until the poll reconciles (a brand-new
                    // tag has no row yet — it simply appears when the fresh payload lands).
                    reconcilingTags.value = new Set(reconcilingTags.value).add(nameLc);
                    pickerOpen.value = false;
                    newTagSaving.value = false;
                    // The tag write also changes the banking recentTransactions payload, but
                    // nothing here refreshes it — leaving the cached bankInfo (and the picker
                    // it feeds) stale. Background-poll it until the change lands, and flag
                    // other pages to SWR+poll on their next mount.
                    pendingBankingRevalidate.value = true;
                    getBankingInfo(null, false, null, /* staleWhileRevalidate */ true, /* pollUntilChanged */ true);
                    if (isExistingTag) {
                        // EDIT: the tag list is unchanged (same ids), so its hash never flips —
                        // polling the list would run to max attempts. The change lives in the
                        // tag's own /Transactions payload, so poll THAT. Keep all caches intact:
                        // the edited tag's cached transactions are the poll baseline, so don't
                        // wipe the very entry we're polling.
                        reconcileEditedTag(nameLc);
                    }
                    else {
                        // CREATE: a new tag id appears in the list — poll the list. Bust the
                        // per-tag /Transactions caches so totals refetch fresh, but keep the
                        // list entry as the pollUntilChanged baseline.
                        clearCustomerTagsCache(/* preserveList */ true);
                        onLoad(true);
                    }
                    return;
                }
                const t = targets[i];
                const existing = (t.customerTags ?? []).map((ct) => ({ tag: ct.tag, id: ct.id }));
                const isOn = newTagSelectedIds.value.has(t.id);
                let nextTags;
                if (isOn) {
                    // Add — guard against dupes if the transaction somehow already has it.
                    if (existing.some((e) => (e.tag ?? '').toLowerCase() === nameLc)) {
                        processNext(i + 1);
                        return;
                    }
                    nextTags = [...existing, { tag: name }];
                }
                else {
                    // Remove every matching entry (handles merged-duplicate ids on one tx).
                    nextTags = existing.filter((e) => (e.tag ?? '').toLowerCase() !== nameLc);
                }
                transactionsApi.updateTransactionTags(t.bankAccountID, t.id, nextTags, () => processNext(i + 1));
            };
            processNext(0);
        }
        // Reconcile an edited (existing) tag after adding/removing transactions. The tag
        // list doesn't change on an edit, so the list hash never flips — instead we poll
        // the tag's own /Transactions endpoint (whose payload, and server hash, does
        // change) until the server reflects the write, then rebuild from the now-fresh
        // caches. A tag merged from duplicate records has several backing ids; poll each
        // and rebuild on the first one that lands a changed (non-stale) payload.
        function reconcileEditedTag(nameLc) {
            const entry = (customerTags.value ?? []).find((t) => (t.tag ?? '').toLowerCase() === nameLc);
            const ids = entry?.ids?.length ? entry.ids : (entry?.id ? [entry.id] : []);
            if (ids.length === 0) {
                onLoad();
                return;
            }
            let done = false;
            ids.forEach(id => {
                getTransactionsForCustomerTag(id, (_gt, isStale) => {
                    // Skip the stale (pre-write) emission. Rebuild once the changed payload
                    // lands — or when the poll exhausts, which also fires a non-stale
                    // callback so the skeleton never gets stuck.
                    if (isStale || done)
                        return;
                    done = true;
                    onLoad();
                }, /* staleWhileRevalidate */ true, /* pollUntilChanged */ true);
            });
        }
        function onLoad(revalidate = false) {
            getCustomer(() => {
                // isMoneyGuided (toggle gate) needs the employer in shared state.
                if (state.customer?.employerID)
                    getEmployer(state.customer.employerID);
                getCustomerTags((result, isStale) => {
                    // During a post-write poll the stale callback is the pre-write snapshot;
                    // skip the per-tag rebuild and wait for the fresh (hash-changed) one.
                    if (revalidate && isStale)
                        return;
                    const raw = (result ?? []);
                    if (raw.length === 0) {
                        customerTags.value = [];
                        reconcilingTags.value = new Set();
                        triggerBarAnimation();
                        return;
                    }
                    // Backend can return multiple records with the same tag name (different
                    // ids). Merge by lower-cased name so the UI shows one bubble per tag —
                    // keep all ids so delete can fan out across the duplicates.
                    const merged = new Map();
                    raw.forEach((t) => {
                        const key = (t.tag ?? '').toLowerCase();
                        const entry = merged.get(key) ?? { id: t.id, ids: [], tag: t.tag, total: 0, groupedTransactions: [], _seenTxIds: new Set() };
                        entry.ids.push(t.id);
                        merged.set(key, entry);
                    });
                    let remaining = raw.length;
                    raw.forEach((rawTag) => {
                        getTransactionsForCustomerTag(rawTag.id, (gt) => {
                            const entry = merged.get((rawTag.tag ?? '').toLowerCase());
                            if (entry) {
                                (gt ?? []).forEach((g) => {
                                    const groupOut = [];
                                    g.forEach((tx) => {
                                        if (entry._seenTxIds.has(tx.id))
                                            return;
                                        entry._seenTxIds.add(tx.id);
                                        groupOut.push(tx);
                                        entry.total += tx.amount;
                                    });
                                    if (groupOut.length)
                                        entry.groupedTransactions.push(groupOut);
                                });
                            }
                            remaining--;
                            if (remaining === 0) {
                                // Assign once, after all per-tag fetches have folded their
                                // transactions in — keeps the computed bubble size / colour
                                // derivations reactive (mutating the raw merged objects mid-
                                // flight would bypass Vue's reactive proxy on the array).
                                const tags = Array.from(merged.values());
                                tags.forEach(t => delete t._seenTxIds);
                                customerTags.value = tags;
                                reconcilingTags.value = new Set();
                                triggerBarAnimation();
                            }
                        });
                    });
                }, /* refreshCache */ false, /* staleWhileRevalidate */ revalidate, /* pollUntilChanged */ revalidate);
            });
        }
        // Bars start at 0% width and animate to their target on next paint, so the
        // race feels alive when the page reveals.
        function triggerBarAnimation() {
            barsAnimated.value = false;
            nextTick(() => {
                requestAnimationFrame(() => { barsAnimated.value = true; });
            });
        }
        onMounted(() => {
            onLoad();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: "#Tagged Transactions",
                    info: true,
                    onInfo: _cache[0] || (_cache[0] = ($event) => (infoOpen.value = true))
                }),
                _createVNode(CustomerLoadWait, { customer: customerTags.value }, null, 8, ["customer"]),
                (customerTags.value && customerTags.value.length == 0 && pendingNewTags.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                        _cache[9] || (_cache[9] = _createElementVNode("p", null, "You have not added any #tags yet — create your first one now!", -1)),
                        _createVNode(MGButton, {
                            class: "mt-3",
                            noCaps: true,
                            text: "Create a tag",
                            onClick: openCreateTag
                        })
                    ]))
                    : _createCommentVNode("", true),
                ((sortedTags.value && sortedTags.value.length > 0) || pendingNewTags.value.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        (viewMode.value === 'bars' || !_unref(isMoneyGuided))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pendingNewTags.value, (name) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        key: 'pending-' + name,
                                        class: "raceRow raceRow-pending"
                                    }, _cache[10] || (_cache[10] = [
                                        _createStaticVNode("<div class=\"raceTop\" data-v-446303a2><div class=\"raceTagSkeleton\" data-v-446303a2></div><div class=\"raceAmountSkeleton\" data-v-446303a2></div></div><div class=\"raceTrack\" data-v-446303a2><div class=\"raceBarSkeleton\" data-v-446303a2></div></div>", 2)
                                    ])));
                                }), 128)),
                                _createVNode(SwipeActionsContainer, {
                                    ref_key: "swipeContainer",
                                    ref: swipeContainer,
                                    items: sortedTags.value ?? [],
                                    preview: true,
                                    backgroundColor: 'var(--background-color-alt)',
                                    actions: [
                                        {
                                            image: require('@/assets/trashcan.png'),
                                            type: 'delete',
                                            backgroundColor: 'var(--background-color-alt6)'
                                        }
                                    ],
                                    onActionClick: handleAction
                                }, {
                                    default: _withCtx(({ item: t }) => [
                                        _createElementVNode("div", {
                                            class: "raceRow",
                                            onClick: ($event) => (isReconciling(t) ? null : openSpotlight(t))
                                        }, [
                                            _createElementVNode("div", _hoisted_5, [
                                                _createElementVNode("span", _hoisted_6, "#" + _toDisplayString(t.tag), 1),
                                                (isReconciling(t))
                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_7))
                                                    : (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_unref(state).formatter.format(Math.abs(t.total))), 1))
                                            ]),
                                            _createElementVNode("div", _hoisted_9, [
                                                (isReconciling(t))
                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_10))
                                                    : (_openBlock(), _createElementBlock("div", {
                                                        key: 1,
                                                        class: "raceBar",
                                                        style: _normalizeStyle(raceBarStyle(t))
                                                    }, [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tagSegments(t), (seg, i) => {
                                                            return (_openBlock(), _createElementBlock("div", {
                                                                key: i,
                                                                class: "raceBarSeg",
                                                                style: _normalizeStyle({ width: seg.pct + '%', background: seg.color })
                                                            }, null, 4));
                                                        }), 128))
                                                    ], 4))
                                            ])
                                        ], 8, _hoisted_4)
                                    ]),
                                    _: 1
                                }, 8, ["items", "actions"]),
                                showCreateTagCta
                                    ? (_openBlock(), _createElementBlock("div", {
                                        key: 0,
                                        class: "raceRow raceRow-create",
                                        onClick: openCreateTag
                                    }, _cache[11] || (_cache[11] = [
                                        _createElementVNode("div", { class: "raceCreateInner" }, [
                                            _createElementVNode("span", { class: "raceCreatePlus" }, "+"),
                                            _createElementVNode("span", { class: "raceCreateLabel" }, "Create a new tag")
                                        ], -1)
                                    ])))
                                    : _createCommentVNode("", true)
                            ]))
                            : (_openBlock(), _createElementBlock("div", {
                                key: 1,
                                ref_key: "cloudWrapRef",
                                ref: cloudWrapRef,
                                class: "tagCloudChart"
                            }, [
                                _createVNode(_unref(VChart), {
                                    option: cloudOption.value,
                                    autoresize: "",
                                    class: "cloudChart",
                                    onClick: onCloudClick
                                }, null, 8, ["option"])
                            ], 512))
                    ]))
                    : _createCommentVNode("", true),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: spotlightOpen.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((spotlightOpen).value = $event)),
                    title: selectedTag.value ? '#' + selectedTag.value.tag : '',
                    height: "85vh",
                    closeButton: true
                }, {
                    default: _withCtx(() => [
                        (selectedTag.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(state).formatter.format(Math.abs(selectedTag.value.total))), 1),
                                _cache[14] || (_cache[14] = _createElementVNode("div", { class: "spotlightTotalLabel" }, "total tagged spend", -1)),
                                (topMerchants.value.length)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                        _cache[12] || (_cache[12] = _createElementVNode("div", { class: "spotlightSectionTitle" }, "Top merchants", -1)),
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(topMerchants.value, (m) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                                key: m.name,
                                                class: "merchantRow"
                                            }, [
                                                _createElementVNode("span", _hoisted_14, _toDisplayString(m.name), 1),
                                                _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(state).formatter.format(m.total)), 1)
                                            ]));
                                        }), 128))
                                    ]))
                                    : _createCommentVNode("", true),
                                _createElementVNode("div", _hoisted_16, [
                                    _cache[13] || (_cache[13] = _createElementVNode("div", { class: "spotlightSectionTitle" }, "Transactions", -1)),
                                    _createVNode(TransactionsList, {
                                        showDateTotal: true,
                                        transactions: selectedTag.value.groupedTransactions,
                                        formatter: _unref(state).formatter,
                                        onTransactionClick: goToTransaction,
                                        emptyDescription: "Go forth and #tag!"
                                    }, null, 8, ["transactions", "formatter"])
                                ]),
                                showEditTagCta
                                    ? (_openBlock(), _createBlock(MGButton, {
                                        key: 1,
                                        fullWidth: true,
                                        noCaps: true,
                                        outline: true,
                                        text: "Edit",
                                        onClick: _cache[1] || (_cache[1] = ($event) => (openAddToTag(selectedTag.value)))
                                    }))
                                    : _createCommentVNode("", true)
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"]),
                _createVNode(MGDialog, {
                    modelValue: nameDialogOpen.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((nameDialogOpen).value = $event)),
                    title: "Create a tag",
                    contentHeight: "120px"
                }, {
                    content: _withCtx(() => [
                        _createVNode(MGTextField, {
                            modelValue: newTagName.value,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((newTagName).value = $event)),
                            label: "Tag name",
                            variant: "solo",
                            flat: true,
                            "hide-details": "",
                            "single-line": "",
                            "bg-color": "#eeeeee",
                            prefix: "#",
                            autofocus: "",
                            onKeyup: _withKeys(confirmTagName, ["enter"])
                        }, null, 8, ["modelValue"]),
                        (nameIsDuplicate.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, "A tag called #" + _toDisplayString(trimmedNewTagName.value) + " already exists.", 1))
                            : _createCommentVNode("", true)
                    ]),
                    actions: _withCtx(() => [
                        _createVNode(MGButton, {
                            fullWidth: true,
                            noCaps: true,
                            disabled: !trimmedNewTagName.value || nameIsDuplicate.value,
                            text: "Next",
                            onClick: confirmTagName
                        }, null, 8, ["disabled"])
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: pickerOpen.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((pickerOpen).value = $event)),
                    title: '#' + trimmedNewTagName.value,
                    height: "95vh",
                    closeButton: true
                }, {
                    footer: _withCtx(() => [
                        _createVNode(MGButton, {
                            fullWidth: true,
                            noCaps: true,
                            disabled: !canSaveNewTag.value,
                            text: saveButtonText.value,
                            onClick: saveNewTag
                        }, null, 8, ["disabled", "text"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_18, [
                            _createVNode(TransactionsList, {
                                transactions: pickerDisplayTransactions.value,
                                formatter: _unref(state).formatter,
                                today: _unref(state).today,
                                yesterday: _unref(state).yesterday,
                                search: true,
                                allowSearchBack: true,
                                dividingLines: true,
                                categoryPills: true,
                                selectable: true,
                                selectedIds: newTagSelectedIds.value,
                                onTransactionClick: toggleNewTagTransaction,
                                onSearchBack: onPickerSearchBack,
                                onCleared: _cache[5] || (_cache[5] = ($event) => (buildPickerTransactions()))
                            }, null, 8, ["transactions", "formatter", "today", "yesterday", "selectedIds"])
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: infoOpen.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((infoOpen).value = $event)),
                    title: "#Spending, Your Way",
                    height: "40vh",
                    closeButton: true
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_19, [
                            _cache[15] || (_cache[15] = _createElementVNode("div", { class: "info-drawer-description" }, "Ever wish you could easily see how much you're spending on specific things, like that awesome weekend getaway or your home renovation project? Custom #tags allow you to do just that.", -1)),
                            _createVNode(MGButton, {
                                text: "Close",
                                onClick: _cache[7] || (_cache[7] = ($event) => (infoOpen.value = false))
                            })
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue"])
            ], 64));
        };
    }
});
